import IconCatch from '@/components/icons/icon-catch/icon-catch';
import MarkdownTextTailwind from '@/components/markdown/markdown-text-tailwind';
import useAnalyticsCalls from '@/hooks/use-analytics';
import useLoadMoreSources from '@/hooks/use-load-more-sources';
import { RiskFactor, RiskFactorType } from '@/repositories/risk.repository';
import { CloseButton } from '@/components/icons/close-button';
import { useEffect } from 'react';
import SourceQuoteTag from '../source-quote/source-quote';
import NonQuantifiableRiskFactor from './components/non-quantifiable-risk-factor/non-quantifiable-risk-factor';
import QuantifiableRiskFactor from './components/quantifiable-risk-factor/quantifiable-risk-factor';

type ModalDetailRiskFactor = {
  isOpen: boolean;
  onClose: () => void;
  riskFactor: RiskFactor;
  affectedCancerTypes: string[];
  impactsByCancerType: Record<string, number>;
};

export default function ModalDetailRiskFactor({
  isOpen,
  onClose,
  riskFactor,
  affectedCancerTypes,
  impactsByCancerType,
}: ModalDetailRiskFactor) {
  const analytics = useAnalyticsCalls();

  useEffect(() => {
    if (isOpen) analytics.callRiskFactorViewed(riskFactor.label, riskFactor.id);
  }, [isOpen]);

  const { label, whyItMatters, shortDescription, sources } = riskFactor;

  const { visibleSources, showLoadMoreButton, handleLoadMoreSources } = useLoadMoreSources(isOpen, sources);

  const isNonQuantifiable =
    riskFactor.riskFactorType === RiskFactorType.nonQuantifiablePositive ||
    riskFactor.riskFactorType === RiskFactorType.nonQuantifiableNegative;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="dashboard-reset modal-backdrop" onClick={onClose}>
        {/* Modal Content */}
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-inner-content">
            {/* Header Section */}
            <div className="modal-header">
              <div className="modal-header-top">
                <div className="modal-title-container">
                  <h1 className="mobile-h2-primary m-0">
                    {label}
                  </h1>
                </div>
                <button
                  onClick={onClose}
                  className="modal-close-button"
                >
                  <CloseButton />
                </button>
              </div>
            </div>

            {/* Divider */}
            <div className="modal-divider" />

            {/* Why it matters section */}
            <div className="modal-section">
              <div className="modal-section-header">
                <div className="modal-section-icon-container">
                  <IconCatch icon="why-it-matters" size="s" transparentBackground />
                </div>
                <h2 className="modal-section-title">
                  Why it matters
                </h2>
              </div>
              <div className="modal-section-content">
                <div className="modal-section-text">
                  <MarkdownTextTailwind>{whyItMatters}</MarkdownTextTailwind>
                </div>
              </div>
            </div>

            {/* Risk Factor details section */}
            {isNonQuantifiable ? (
              <NonQuantifiableRiskFactor />
            ) : (
              <QuantifiableRiskFactor
                shortDescription={shortDescription}
                riskFactor={riskFactor}
                affectedCancerTypes={affectedCancerTypes}
                impactsByCancerType={impactsByCancerType}
              />
            )}

            {/* The Science Section */}
            {sources && sources.length ? (
              <>
                <div className="modal-divider" />
                <div className="modal-science-section">
                  <h3 className="modal-science-title">
                    The science
                  </h3>
                  <SourceQuoteTag
                    sources={sources.slice(0, visibleSources)}
                    showLoadMoreButton={showLoadMoreButton}
                    loadMore={handleLoadMoreSources}
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
